/**
 * RequireFeature — whole-page gate for feature-pack-entitled surfaces.
 *
 * Hiding a nav item is not gating: deep links, bookmarks, and in-app
 * cross-links still reach the route, so every pack-gated route also wraps in
 * this. Grant resolution comes from the shared registry via useFeatureGate
 * (docs/FEATURE_PACKS_PLAN.md).
 *
 * Loading contract: while the tenant doc loads, packs resolve to
 * all-features, so a fully-entitled tenant never flashes the denial. A
 * restricted tenant flips to the denial when the doc lands.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { PackageX } from 'lucide-react';
import type { FeatureKey } from '@shared/domain/feature-packs';
import { useFeatureGate } from '@/hooks/useFeatureGate';

export function RequireFeature({
  feature,
  children,
  surface = 'This area',
}: {
  feature: FeatureKey;
  children: ReactNode;
  /** Named in the denial copy, e.g. "Customer services". */
  surface?: string;
}) {
  const granted = useFeatureGate(feature);

  if (!granted) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <PackageX className="w-12 h-12 text-theme-muted mx-auto mb-3" />
          <h2 className="text-lg font-bold text-theme-primary">
            Not part of your plan
          </h2>
          <p className="text-sm text-theme-muted mt-1">
            {surface} isn&apos;t included in your current feature packs. Get
            in touch if you&apos;d like it added.
          </p>
          <Link
            to="/home"
            className="inline-block mt-4 px-5 py-2 rounded-full bg-surface-hover text-sm text-theme-primary transition-colors"
          >
            Back to dashboard
          </Link>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
